import { apiClient } from './api';
import type { User } from '../types/auth';
import type {
  StripeCustomerLink,
  DiscordProfile,
  ChangePasswordRequest,
  SetPasswordRequest,
  DeleteAccountRequest,
} from '../types/profile';

export const profileService = {
  // ===== User Profile =====

  /**
   * Update user profile (username/name/email)
   * PATCH /api/users/me/
   */
  async updateProfile(data: Partial<Pick<User, 'username' | 'name' | 'email'>>): Promise<User> {
    return apiClient.patch<User>('/api/users/me/', data);
  },

  /**
   * Change password (for users with existing password)
   * POST /api/users/change-password/
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    return apiClient.post('/api/users/change-password/', data);
  },

  /**
   * Set password (for external auth users without a password)
   * POST /api/users/set-password/
   */
  async setPassword(data: SetPasswordRequest): Promise<{ message: string }> {
    return apiClient.post('/api/users/set-password/', data);
  },

  /**
   * Delete account
   * DELETE /api/users/delete-account/
   */
  async deleteAccount(data: DeleteAccountRequest): Promise<void> {
    return apiClient.delete('/api/users/delete-account/', data);
  },

  /**
   * Upload avatar image
   * POST /api/users/me/avatar/
   */
  async uploadAvatar(file: File): Promise<User> {
    const formData = new FormData();
    formData.append('avatar', file);
    return apiClient.post<User>('/api/users/me/avatar/', formData);
  },

  /**
   * Delete avatar image
   * DELETE /api/users/me/avatar/
   */
  async deleteAvatar(): Promise<User> {
    return apiClient.delete<User>('/api/users/me/avatar/');
  },

  // ===== Stripe/Billing =====

  /**
   * List all Stripe customer links with subscriptions and purchases
   * GET /api/stripe/customer-links/
   */
  async getStripeCustomerLinks(): Promise<StripeCustomerLink[]> {
    return apiClient.get<StripeCustomerLink[]>('/api/stripe/customer-links/');
  },

  /**
   * Add Stripe customer link
   * POST /api/stripe/customer-links/
   */
  async addStripeCustomer(customerId: string): Promise<void> {
    return apiClient.post('/api/stripe/customer-links/', {
      stripe_customer_id: customerId,
    });
  },

  /**
   * Link from checkout session (post-purchase flow)
   * POST /api/stripe/customer-links/link_from_session/
   */
  async linkFromSession(sessionId: string): Promise<void> {
    return apiClient.post('/api/stripe/customer-links/link_from_session/', {
      session_id: sessionId,
    });
  },

  /**
   * Sync latest data from Stripe
   * POST /api/stripe/customer-links/:id/sync/
   */
  async syncStripeCustomer(linkId: number): Promise<void> {
    return apiClient.post(`/api/stripe/customer-links/${linkId}/sync/`);
  },

  /**
   * Set primary customer
   * POST /api/stripe/customer-links/:id/set_primary/
   */
  async setPrimaryCustomer(linkId: number): Promise<void> {
    return apiClient.post(`/api/stripe/customer-links/${linkId}/set_primary/`);
  },

  /**
   * Delete Stripe customer link
   * DELETE /api/stripe/customer-links/:id/
   */
  async deleteStripeCustomer(linkId: number): Promise<void> {
    return apiClient.delete(`/api/stripe/customer-links/${linkId}/`);
  },

  /**
   * Create billing portal session
   * POST /api/stripe/customer-links/:id/billing_portal/
   */
  async createBillingPortalSession(linkId: number, returnUrl?: string): Promise<{ url: string }> {
    return apiClient.post(`/api/stripe/customer-links/${linkId}/billing_portal/`, {
      return_url: returnUrl,
    });
  },

  // ===== Discord =====

  /**
   * Get Discord status
   * GET /api/discord/
   * Returns 404 if not linked
   */
  async getDiscordProfile(): Promise<DiscordProfile | null> {
    try {
      return await apiClient.get<DiscordProfile>('/api/discord/');
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get Discord OAuth authorization URL
   * GET /api/discord/oauth/authorize/
   * Redirects user to Discord for authorization
   */
  async getDiscordAuthUrl(): Promise<{ authorize_url: string }> {
    return apiClient.get<{ authorize_url: string }>('/api/discord/oauth/authorize/');
  },

  /**
   * Link Discord account (DEPRECATED - use getDiscordAuthUrl instead)
   * POST /api/discord/
   */
  async linkDiscord(discordId: string): Promise<DiscordProfile> {
    return apiClient.post<DiscordProfile>('/api/discord/', {
      discord_id: discordId,
    });
  },

  /**
   * Create new invite
   * POST /api/discord/create_invite/
   */
  async createDiscordInvite(): Promise<DiscordProfile> {
    return apiClient.post<DiscordProfile>('/api/discord/create_invite/');
  },

  /**
   * Unlink Discord
   * DELETE /api/discord/
   */
  async unlinkDiscord(): Promise<void> {
    return apiClient.delete('/api/discord/');
  },

  // ===== Frontend State =====

  /**
   * Get UI preferences
   * GET /api/users/state/
   */
  async getFrontendState(): Promise<{ frontend_state: Record<string, any> }> {
    return apiClient.get('/api/users/state/');
  },

  /**
   * Save UI preferences (merges with existing)
   * PATCH /api/users/state/
   */
  async saveFrontendState(state: Record<string, any>): Promise<{ frontend_state: Record<string, any> }> {
    return apiClient.patch('/api/users/state/', {
      frontend_state: state,
    });
  },
};
